// 12 sec Product Hero Shot's form (live's `ProductHeroShotForm`, spec G14-templates): the product
// and its packshot, the brand's logo for the end card, the style and the ratio; the server's
// director writes the spec, then two keyframes and a 12-second clip. One tile.
import { CloseButton, Group, Image, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Archive } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { useGetBrand, useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { Product, TemplateSource } from "../../../api/generated/model";
import { VideoTemplateQuote, VideoTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { ProductPicker } from "../../../platform/media/ProductPicker";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { CostChip } from "../TemplateCost";
import { RatioGlyph } from "../../../ui/RatioGlyph";
import { selectUpTo } from "../../../ui/format";
import classes from "./forms.module.css";
import { CUSTOM, DIRECTOR, HeroStylePicker } from "./HeroStylePicker";
import {
  exampleImage,
  mainPicture,
  ReuseSwitch,
  StartArrow,
  type ExampleImage,
  type FormExample,
  type TemplateFormProps,
} from "./kit";

/** Live's words (`constants.ts`). */
const TITLE = "12 sec Product Hero Shot";
const PRODUCT_TIP = "The product this ad is for. Pick it from your team's product list";
/** Live's products list's line for one product (`ProductsList`, capture 27), in the singular. */
const PICK_ONE = selectUpTo(1, "product");
const RATIO_TIP = "Pick your output's dimensions from the list of aspect ratios.";
const HANDOFF = "Your video is finishing in My Generations — you can close this panel now.";
const PICK_PRODUCT = "Pick a product";
const NO_BRIEF = "This product has no description to brief the director with.";
const NO_PACKSHOT = "Select one picture of the product as the packshot.";
const SHORT_STYLE = "Describe the style in at least 10 characters";
const LOADING_MODEL = "Loading the generation model…";
/** Ours (live bounds none): the brief that keeps the director's prompt under its row's cap. */
const MAX_BRIEF = 60_000;
const LONG_BRIEF = "This product's description is too long to brief the director: at most 60,000 characters.";
/** Live's ratios (`ASPECT_RATIO_OPTIONS`), in its order, and its default. */
const RATIOS = ["9:16", "16:9", "1:1", "3:4", "4:3"] as const;
type Ratio = (typeof RATIOS)[number];
/** Live's `MIN_PROMPT_LENGTH` for a Custom style. */
const MIN_CUSTOM = 10;

/** The product: one of the brand's with its packshot, or the card's example's packshot and brief. */
type ProductPick =
  | { kind: "product"; id: string; packshot: string }
  | { kind: "example"; image: ExampleImage; brief: string };

interface Values {
  product: ProductPick | null;
  brandLogo: boolean;
  style: string;
  customStyle: string;
  ratio: Ratio;
}

const EMPTY: Values = { product: null, brandLogo: false, style: DIRECTOR, customStyle: "", ratio: "9:16" };

/** Live's brief: the product's preprocessed one, else its description, trimmed. */
export function briefOf(product: Product): string {
  return product.ai_description?.trim() || product.description?.trim() || "";
}

/** The form's values: the card's example's (live's OVERDRIVE packshot and brief, Anime, 9:16). */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "product-hero-shot") return null;
  const image = exampleImage(example, inputs.packshot);
  return {
    product: image === null ? null : { kind: "example", image, brief: inputs.brief },
    brandLogo: false,
    style: inputs.style ?? DIRECTOR,
    customStyle: inputs.custom_style ?? "",
    ratio: (RATIOS as readonly string[]).includes(inputs.aspect_ratio ?? "") ? (inputs.aspect_ratio as Ratio) : "9:16",
  };
}

/**
 * Live's form: "Reuse example" (on fills the card's example, off clears the form; Replicate opens
 * it on), the "Product" tile (live's `product-tile-field`: the Products picker, where the member
 * picks the product, one of its pictures as the packshot, and the brand's logo for the end card
 * where the brand has one), and in the footer the style picker and the ratio, then the round
 * start: off until there is a product with a brief and a packshot, and a Custom style is long
 * enough. Each field says what is wrong with it (live's form shows only the product's words).
 * Once started, live's hand-off line.
 */
export function HeroShotForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, options, onStarted } = props;
  const catalog = useGetCatalog().data;
  const products = useListProducts(teamId, brandId).data ?? [];
  const brand = useGetBrand(teamId, brandId).data;
  const [collapsed, setCollapsed] = useState(false);
  const [picking, setPicking] = useState(false);
  // Live's hand-off line closes with its ×; it shows again for the next start.
  const [handoffClosed, setHandoffClosed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [values, setValues] = useState<Values>(start ?? EMPTY);
  const set = (changes: Partial<Values>) => setValues((current) => ({ ...current, ...changes }));
  const fill = (on: boolean) => {
    const filled = on ? valuesOf(example) : null;
    setReuse(filled !== null);
    setValues(filled ?? EMPTY);
  };
  const pick = values.product;
  const product = pick?.kind === "product" ? products.find((each) => each.id === pick.id) : undefined;
  const brief = pick?.kind === "example" ? pick.brief : product === undefined ? "" : briefOf(product);
  // A brand's product's packshot while it is still one of the product's pictures.
  const packshot: TemplateSource | null =
    pick?.kind === "example"
      ? { kind: "example", key: pick.image.exampleKey }
      : pick === null || product?.images.some((each) => each.asset_id === pick.packshot) !== true
        ? null
        : { kind: "asset", asset_id: pick.packshot };
  const logo = brand?.logo_asset_id ?? null;
  const shortStyle = values.style === CUSTOM && values.customStyle.trim().length < MIN_CUSTOM;
  const productError =
    pick === null
      ? PICK_PRODUCT
      : brief === ""
        ? NO_BRIEF
        : brief.length > MAX_BRIEF
          ? LONG_BRIEF
          : packshot === null
            ? NO_PACKSHOT
            : null;
  const inputs =
    productError !== null || shortStyle || packshot === null
      ? null
      : {
          template: "product-hero-shot" as const,
          ...(pick?.kind === "product" ? { product_id: pick.id } : {}),
          packshot,
          brief,
          ...(values.brandLogo && logo !== null ? { brand_logo: { kind: "asset" as const, asset_id: logo } } : {}),
          style: values.style,
          custom_style: values.customStyle,
          aspect_ratio: values.ratio,
        };
  const body = inputs === null ? null : { type: "video.template" as const, inputs };
  const request = VideoTemplateRequest.safeParse(body);
  const quote = VideoTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted: (job) => {
      setHandoffClosed(false);
      onStarted(job);
    },
  });
  const busy = job.start.isPending;
  const shown =
    pick?.kind === "example"
      ? pick.image.url
      : (product?.images.find((each) => each.asset_id === pick?.packshot)?.url ?? null);
  const toggle = () => setPicking((open) => !open);
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} />}
      summary={<Text size="sm">{TITLE}</Text>}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={4} wrap="nowrap">
            {catalog === undefined ? (
              <Text size="xs" c="var(--cai-color-text-muted)">
                {LOADING_MODEL}
              </Text>
            ) : null}
            <HeroStylePicker
              style={values.style}
              customStyle={values.customStyle}
              options={options}
              disabled={busy}
              onApply={(style, customStyle) => set({ style, customStyle })}
            />
            <Choice
              setting="Aspect ratio"
              icon={<RatioGlyph ratio={values.ratio} />}
              tip={RATIO_TIP}
              width={88}
              chevron={false}
              value={values.ratio}
              options={RATIOS.map((each) => ({ value: each, label: each, icon: <RatioGlyph ratio={each} /> }))}
              onChange={(ratio) => set({ ratio: ratio as Ratio })}
            />
          </Group>
          <Group gap={8} wrap="nowrap">
            {/* Live's: one clip a Generate (`product-hero-shot-form/.../content/index.tsx:45`). */}
            <CostChip estimate={props.template.estimate} outputs={1} />
            <StartArrow canStart={job.canStart && catalog !== undefined} starting={busy} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        {/* Live's Products picker (`ProductAssetsSelect`, one product, one picture, the Brand Logo
            alone): it reopens on the product picked, its packshot and the logo's switch; the
            example's packshot is no product of the brand's, so it opens empty. */}
        <ProductPicker
          teamId={teamId}
          brandId={brandId}
          chosen={
            pick?.kind === "product" && product !== undefined
              ? { productId: pick.id, assetId: pick.packshot, url: shown, name: product.name }
              : null
          }
          subtitle={PICK_ONE}
          typography={false}
          colors={false}
          brandLogo={{ available: logo !== null, url: brand?.logo_url ?? null, on: values.brandLogo }}
          preselect={mainPicture}
          opened={picking}
          onClose={() => setPicking(false)}
          onConfirm={(choice, assets) =>
            set({
              product: { kind: "product", id: choice.productId, packshot: choice.assetId },
              brandLogo: assets.brandLogo,
            })
          }
          anchor={(wrap) =>
            shown === null ? (
              // Live's compact `PlaceholderTile`, its tooltip while it is empty.
              <Tooltip label={PRODUCT_TIP} multiline maw={280} withArrow>
                {wrap(
                  <UnstyledButton className={classes.productTile} aria-label="Product" disabled={busy} onClick={toggle}>
                    <Archive size={20} strokeWidth={1.5} aria-hidden />
                    <span>Product</span>
                  </UnstyledButton>,
                )}
              </Tooltip>
            ) : (
              // Live's filled tile (`RemovableImageTile`): the packshot, which reopens the picker,
              // ringed while the product lacks something, and its ×, which clears the product and
              // the logo; no tooltip once filled.
              <span className={classes.productFilled} data-invalid={productError === null ? undefined : true}>
                {wrap(
                  <UnstyledButton className={classes.productOpen} aria-label="Product" disabled={busy} onClick={toggle}>
                    <Image src={shown} alt="The packshot" className={classes.productPicture} />
                  </UnstyledButton>,
                )}
                <CloseButton
                  size="xs"
                  className={classes.productRemove}
                  aria-label="Remove the product"
                  disabled={busy}
                  onClick={() => set({ product: null, brandLogo: false })}
                />
              </span>
            )
          }
        />
        {/* Live's words under the tile, once a product is picked (live's validates on change). */}
        {productError === null || pick === null ? null : (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {productError}
          </Text>
        )}
        {shortStyle ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {SHORT_STYLE}
          </Text>
        ) : null}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's hand-off line: the run is the server's, so the panel may close. */}
        {job.start.isSuccess && !handoffClosed ? (
          <Group gap={6} wrap="nowrap" align="flex-start">
            <Text component="output" size="sm">
              {HANDOFF}
            </Text>
            <CloseButton size="xs" aria-label="Dismiss" onClick={() => setHandoffClosed(true)} />
          </Group>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}
